import FilledButtonM from "@common/components/button/FilledButtonM"
import { FormStepper } from "@common/components/stepper/FormStepper"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { ScreenSkeleton } from "@common/screens/ScreenSkeleton"
import { theme } from "@common/theme/theme"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { authService } from "@data/services/authService"
import { liveService } from "@data/services/liveService"
import { tutorAvailabilityService } from "@data/services/tutorAvailabilityService"
import { useNavigation } from "@react-navigation/native"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import { showReleaseLossWarning } from "./maDispoDialogs"
import { slotKeyOf } from "./maDispoSlot"
import {
    RELEASE_MOTIF,
    buildReleaseBody,
    engagedSlotLabel,
    engagedSlotOfLive,
    engagedSlotsOf,
    releasedSlotsOf,
    openSlotsOf,
    studentsAtRiskCount,
    withSlot,
} from "./releaseSlotsModel"
import {
    ReleaseSlotsStudentsStep,
    ReleaseSlotsWeekStep,
} from "./ReleaseSlotsSteps"

const StepTitle = ({ children }) => (
    <TitleM style={{ marginTop: 10 }} fontWeight="ExtraBold">
        {children}
    </TitleM>
)

/**
 * « Libérer des créneaux » — the tutor gives up créneaux that carry his lives.
 *
 * One week step (mark what he can no longer give, confirm the rest), then his
 * students, one card each, behind a warning when the release may cost him some.
 * The server decides what happens to each student; the
 * tutor reads its verdict (the preview) and can only open a créneau his family
 * gives to keep a student, which asks the preview again. Nothing is written before
 * « Confirmer ». His one-off engagements on the freed créneaux (remplacements,
 * trials) are not asked about: he is taken to be unavailable for them too. The
 * motif is read from the gesture: « J'arrête tous mes créneaux » is « J'arrête
 * Masteur », anything else a change of schedule.
 */
const ReleaseSlotsScreen = (props) => {
    const navigation = useNavigation()
    const preselected = props.route?.params?.slot
    // Opened from a live's absence flow: its créneau is read off a week fetched now.
    const preselectedLive = props.route?.params?.liveSessionId
    // A trial or a stage holds no créneau of the week: the server frees the live's own hour.
    const oneOffLiveLabel = props.route?.params?.oneOffLabel
    const oneOffLiveIds = oneOffLiveLabel && preselectedLive != null ? [preselectedLive] : []
    const [weekFetched, setWeekFetched] = useState(false)

    const week = props.weekView
    const engagedSlots = useMemo(() => engagedSlotsOf(week), [week])
    const releasedSlots = useMemo(() => releasedSlotsOf(week), [week])
    const serverOpenSlots = useMemo(() => openSlotsOf(week), [week])

    const [selectedKeys, setSelectedKeys] = useState(
        preselected ? [slotKeyOf(preselected)] : []
    )
    const [preselectedKey, setPreselectedKey] = useState(
        preselected ? slotKeyOf(preselected) : null
    )
    const liveSlotResolvedRef = useRef(false)
    useEffect(() => {
        if (!preselectedLive || !weekFetched || liveSlotResolvedRef.current) return
        const slot = engagedSlotOfLive(engagedSlots, preselectedLive)
        if (!slot) return
        liveSlotResolvedRef.current = true
        setSelectedKeys([slotKeyOf(slot.slot)])
        setPreselectedKey(slotKeyOf(slot.slot))
    }, [preselectedLive, weekFetched, engagedSlots])
    const [motif, setMotif] = useState(RELEASE_MOTIF.SCHEDULE_CHANGE)
    const [draftOpenSlots, setDraftOpenSlots] = useState(null)
    const [previewAsked, setPreviewAsked] = useState(false)
    const [done, setDone] = useState(false)
    const [formValues, setFormValues] = useState(null)
    const goToNextStepRef = useRef(null)
    // A preview asked from « Tes élèves » (a créneau opened) stays on that step.
    const advanceOnPreviewRef = useRef(false)

    // The week view is redux-persisted, and so is the last preview: only a
    // preview asked for by THIS flow may decide a step.
    const preview = previewAsked ? props.releasePreview : null
    const openSlots = draftOpenSlots ?? serverOpenSlots
    // Only the créneaux still engaged count: one freed meanwhile is not sent.
    const selected = useMemo(
        () =>
            selectedKeys.filter((key) =>
                engagedSlots.some((s) => slotKeyOf(s.slot) === key)
            ),
        [selectedKeys, engagedSlots]
    )
    const preselectedSlot = engagedSlots.find((s) => slotKeyOf(s.slot) === preselectedKey)
    const preselectedLabel =
        preselectedSlot && selected.includes(preselectedKey)
            ? engagedSlotLabel(preselectedSlot).replace(/^./, (c) => c.toLowerCase())
            : oneOffLiveLabel ?? null

    useEffect(() => {
        props.apiIdle(tutorAvailabilityService.previewSlotRelease)
        props.apiIdle(tutorAvailabilityService.releaseSlots)
        // Ma dispo, underneath, re-seeds its draft on every week fetch: only
        // fetch here when there is no week to start from, or none under us.
        if (!week || preselectedLive) props.apiRequest(tutorAvailabilityService.getAvailability)
    }, [])

    const body = ({ motif: motifOverride, draft } = {}) =>
        buildReleaseBody({
            motif: motifOverride ?? motif,
            selectedSlots: selected,
            serverOpenSlots,
            draftOpenSlots: draft !== undefined ? draft : draftOpenSlots,
            oneOffLiveIds,
        })

    const requestPreview = ({ advance = true, ...overrides } = {}) => {
        advanceOnPreviewRef.current = advance
        setPreviewAsked(true)
        props.apiRequest(tutorAvailabilityService.previewSlotRelease, {}, body(overrides))
    }

    useApiStatus(tutorAvailabilityService.previewSlotRelease, "", true, (data) => {
        if (!advanceOnPreviewRef.current) return
        const advance = () => goToNextStepRef.current?.({ skipStepCustomAction: true })
        const atRisk = data?.refusal ? 0 : studentsAtRiskCount(data)
        if (atRisk > 0) showReleaseLossWarning(atRisk, advance)
        else advance()
    })
    useApiStatus(tutorAvailabilityService.releaseSlots, "", true, () => {
        // The dashboard reads the tutor's lives off the current user.
        props.apiRequest(authService.getUser)
        props.apiRequest(liveService.getLiveSessions)
        setDone(true)
    })
    useApiStatus(tutorAvailabilityService.getAvailability, "", false, () =>
        setWeekFetched(true)
    )

    const getGoToNextStep = useCallback((fn) => {
        goToNextStepRef.current = fn
    }, [])

    const weekStepSlots = useMemo(
        () => [
            ...openSlots.map((slot) => ({ slot, state: "open", engaged: false })),
            // Marked in this draft: struck through, and a second tap gives it back.
            ...engagedSlots.map((s) =>
                selected.includes(slotKeyOf(s.slot)) ? { ...s, freed: true, releasable: true } : s
            ),
            ...releasedSlots.map((s) => ({ ...s, freed: true })),
        ],
        [openSlots, engagedSlots, releasedSlots, selected]
    )

    const toggleEngaged = (weekSlot) => {
        const key = slotKeyOf(weekSlot.slot)
        setMotif(RELEASE_MOTIF.SCHEDULE_CHANGE)
        setSelectedKeys((current) =>
            current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
        )
    }
    const addSlot = (day, time) => setDraftOpenSlots((current) => withSlot(current ?? serverOpenSlots, `${day} ${time}`))
    const removeSlot = (weekSlot) =>
        setDraftOpenSlots((current) =>
            (current ?? serverOpenSlots).filter(
                (s) => slotKeyOf(s) !== slotKeyOf(weekSlot.slot)
            )
        )
    // A créneau his family gives, opened to keep a student: the week grows, the preview runs again.
    const openSuggestedSlot = (slot) => {
        const draft = withSlot(draftOpenSlots ?? serverOpenSlots, slot)
        setDraftOpenSlots(draft)
        requestPreview({ advance: false, draft })
    }
    const stopAll = () => {
        setMotif(RELEASE_MOTIF.UNAVAILABLE)
        requestPreview({ motif: RELEASE_MOTIF.UNAVAILABLE })
    }

    const formData = [
        {
            name: "week",
            type: "custom",
            description: <StepTitle>Ta semaine</StepTitle>,
            component: ReleaseSlotsWeekStep,
            componentProps: {
                slots: weekStepSlots,
                grid: week?.grid ?? {},
                preselectedLabel,
                onAddSlot: addSlot,
                onRemoveSlot: removeSlot,
                onToggleEngaged: toggleEngaged,
                onStopAll: stopAll,
                stopAllLoading: motif === RELEASE_MOTIF.UNAVAILABLE && props.previewLoading,
            },
            showNextButton: selected.length > 0 || oneOffLiveIds.length > 0,
            onNextButtonPress: () => {
                setMotif(RELEASE_MOTIF.SCHEDULE_CHANGE)
                requestPreview({ motif: RELEASE_MOTIF.SCHEDULE_CHANGE })
            },
            nextButtonLoading: motif !== RELEASE_MOTIF.UNAVAILABLE && props.previewLoading,
        },
        {
            name: "students",
            type: "custom",
            description: <StepTitle>Tes élèves</StepTitle>,
            component: ReleaseSlotsStudentsStep,
            componentProps: {
                preview,
                onOpenSlot: openSuggestedSlot,
                previewLoading: props.previewLoading,
            },
            showNextButton: !!preview && !preview.refusal && !props.previewLoading,
        },
    ]

    const onSubmit = () => {
        if (props.releaseLoading || props.previewLoading || !preview || preview.refusal) return
        props.apiRequest(
            tutorAvailabilityService.releaseSlots,
            {},
            { ...body(), cancel_one_offs: true }
        )
    }

    const backToMaDispo = () => {
        // From a live, the absence flow underneath is about a live that is ending.
        if (preselectedLive && navigation.canGoBack()) navigation.pop(2)
        else if (navigation.canGoBack()) navigation.goBack()
        else navigation.replace("tutor/ma-dispo")
    }

    if (done) {
        return (
            <View
                style={{
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    paddingHorizontal: 24,
                    backgroundColor: theme.colors.whiteAlmost,
                }}
            >
                <FontAwesome5
                    name="check-circle"
                    size={44}
                    color={theme.colors.success}
                    style={{ marginBottom: 18 }}
                />
                <TitleM fontWeight="ExtraBold" style={{ textAlign: "center" }}>
                    {`C'est noté, merci ${props.user?.firstname ?? ""}`.trim()}
                </TitleM>
                <TextM
                    fontSize={14}
                    color={theme.colors.grey}
                    wrap
                    center
                    style={{ marginTop: 10, marginBottom: 28, lineHeight: 20 }}
                >
                    Tes créneaux sont libérés. Les familles sont prévenues.
                </TextM>
                <FilledButtonM
                    label={preselectedLive ? "Revenir à mes lives" : "Revenir à Ma dispo"}
                    onPress={backToMaDispo}
                />
            </View>
        )
    }

    return (
        <ScreenSkeleton
            isLoading={!week && !props.loadError}
            errorScreenProps={{
                error: week ? null : props.loadError,
                title: "Impossible de charger ta dispo",
                onButtonPress: () =>
                    props.apiRequest(tutorAvailabilityService.getAvailability),
            }}
        >
            <View style={{ flex: 1, alignItems: "center" }}>
                <FormStepper
                    navigation={navigation}
                    track
                    formData={formData.filter((x) => !x.is_masked)}
                    champion={null}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    onSubmit={onSubmit}
                    submitted={props.releaseLoading}
                    submittedMessage=""
                    submitButtonText="Confirmer"
                    previousRouteName="tutor/ma-dispo"
                    getGoToNextStep={getGoToNextStep}
                />
            </View>
        </ScreenSkeleton>
    )
}

const mapStateToProps = (state) => ({
    user: state.data.currentUser?.object,
    weekView: state.data.tutorAvailability?.weekView,
    releasePreview: state.data.tutorAvailability?.releasePreview,
    loadError: state.api.getAvailability?.error,
    previewLoading: state.api.previewSlotRelease?.request,
    releaseLoading: state.api.releaseSlots?.request,
})

export default connect(mapStateToProps, { apiRequest, apiIdle })(
    ReleaseSlotsScreen
)
